import axios from "axios";
import type {
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import type { LoginResponse, UserAccessCookie } from "~/types/auth";
import type {
  ViewAsRecentSimulation,
  ViewAsStatePayload,
  ViewAsStoredSession,
} from "~/types/viewAs";

const LEGACY_AUTH_COOKIE_DOMAIN = ".teachplus.com.br";
const AUTH_RETRY_LIMIT = 1;
const VIEW_AS_BACKUP_COOKIE = "viewAsBackup";
const VIEW_AS_STATE_COOKIE = "viewAsState";
const VIEW_AS_RECENT_SIMULATIONS_COOKIE = "viewAsRecentSimulations";

const AUTHENTICATION_ERROR_NAME = "RefreshAuthenticationError";
const EXPECTED_UNAUTHORIZED_ERROR_FLAG = "__teachplusExpectedUnauthorized";

type RetryQueueItem = {
  execute: () => Promise<AxiosResponse>;
  resolve: (response: AxiosResponse) => void;
  reject: (error: unknown) => void;
};

type ExpectedUnauthorizedError = {
  [EXPECTED_UNAUTHORIZED_ERROR_FLAG]?: boolean;
};

export interface AuthRequestConfig<D = unknown> extends AxiosRequestConfig<D> {
  requiresAuth?: boolean;
  skipRefreshWait?: boolean;
  skipRefreshRetry?: boolean;
  suppressAuthFailureRedirect?: boolean;
  _retryCount?: number;
}

export interface InternalAuthRequestConfig<
  D = unknown,
> extends InternalAxiosRequestConfig<D> {
  requiresAuth?: boolean;
  skipRefreshWait?: boolean;
  skipRefreshRetry?: boolean;
  suppressAuthFailureRedirect?: boolean;
  _retryCount?: number;
}

let refreshPromise: Promise<void> | null = null;
let retryQueue: RetryQueueItem[] = [];

const getLegacyCookieDomain = (): string => {
  if (typeof window === "undefined") {
    return "";
  }

  const hostname = window.location.hostname;
  const isTeachPlusDomain =
    hostname === "teachplus.com.br" || hostname.endsWith(".teachplus.com.br");

  return isTeachPlusDomain ? LEGACY_AUTH_COOKIE_DOMAIN : "";
};

const buildCookieAttributes = (expiresAt?: Date, includeDomain = false) => {
  const attributes = ["path=/"];
  const domain = includeDomain ? getLegacyCookieDomain() : "";

  if (domain) {
    attributes.push(`domain=${domain}`);
  }

  if (typeof window !== "undefined" && window.location.protocol === "https:") {
    attributes.push("Secure");
  }
  attributes.push("SameSite=Lax");

  if (expiresAt) {
    attributes.push(`expires=${expiresAt.toUTCString()}`);
    const maxAgeSeconds = Math.floor((expiresAt.getTime() - Date.now()) / 1000);
    attributes.push(`Max-Age=${Math.max(maxAgeSeconds, 0)}`);
  }

  return attributes.join("; ");
};

const writeCookie = (
  cookieName: string,
  cookieValue: string | object,
  expiresAt?: Date,
  includeDomain = false,
) => {
  if (typeof document === "undefined") {
    return;
  }

  document.cookie = `${cookieName}=${JSON.stringify(cookieValue)}; ${buildCookieAttributes(
    expiresAt,
    includeDomain,
  )}`;
};

const readCookie = (cookieName: string): unknown => {
  if (typeof document === "undefined") {
    return "";
  }

  const name = `${cookieName}=`;
  const cookies = document.cookie.split(";");

  for (const cookieChunk of cookies) {
    let cookie = cookieChunk;

    while (cookie.charAt(0) === " ") {
      cookie = cookie.substring(1);
    }

    if (!cookie.startsWith(name)) {
      continue;
    }

    const rawValue = cookie.substring(name.length, cookie.length);

    try {
      return JSON.parse(decodeURIComponent(rawValue));
    } catch {
      return rawValue;
    }
  }

  return "";
};

const expireCookie = (cookieName: string) => {
  const expiredAt = new Date(0);

  if (getLegacyCookieDomain()) writeCookie(cookieName, "", expiredAt, true);
  writeCookie(cookieName, "", expiredAt, false);
};

const replaceCookie = (
  cookieName: string,
  cookieValue: string | object,
  expiresAt?: Date,
  includeDomain = false,
) => {
  expireCookie(cookieName);
  writeCookie(cookieName, cookieValue, expiresAt, includeDomain);
};

const toStringCookieValue = (cookieName: string): string => {
  const cookieValue = readCookie(cookieName);
  return typeof cookieValue === "string" ? cookieValue : "";
};

export const createAuthenticationFailureError = (message: string) => {
  const error = new Error(message);
  error.name = AUTHENTICATION_ERROR_NAME;
  return error;
};

export const isAuthenticationFailureError = (error: unknown): boolean => {
  if (error instanceof Error && error.name === AUTHENTICATION_ERROR_NAME) {
    return true;
  }

  if (!axios.isAxiosError(error)) {
    return false;
  }

  return [401, 403].includes(error.response?.status ?? 0);
};

export const markExpectedUnauthorizedError = (error: unknown): void => {
  if (!axios.isAxiosError(error)) {
    return;
  }

  (error as ExpectedUnauthorizedError)[EXPECTED_UNAUTHORIZED_ERROR_FLAG] = true;
};

export const isExpectedUnauthorizedError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) {
    return false;
  }

  return (
    (error as ExpectedUnauthorizedError)[EXPECTED_UNAUTHORIZED_ERROR_FLAG] ===
    true
  );
};

export const createPublicRequestConfig = (
  overrides: AuthRequestConfig = {},
): AuthRequestConfig => ({
  requiresAuth: false,
  skipRefreshWait: true,
  skipRefreshRetry: true,
  ...overrides,
});

export const shouldAttachAuthorization = (
  config?: Pick<AuthRequestConfig, "requiresAuth"> | null,
): boolean => {
  return config?.requiresAuth !== false;
};

export const shouldWaitForRefresh = (
  config?: Pick<AuthRequestConfig, "requiresAuth" | "skipRefreshWait"> | null,
): boolean => {
  return shouldAttachAuthorization(config) && config?.skipRefreshWait !== true;
};

export const shouldRetryAfterUnauthorized = (
  config?: Pick<
    AuthRequestConfig,
    "requiresAuth" | "skipRefreshRetry" | "_retryCount"
  > | null,
): boolean => {
  if (!shouldAttachAuthorization(config)) {
    return false;
  }

  if (config?.skipRefreshRetry === true) {
    return false;
  }

  return (config?._retryCount ?? 0) < AUTH_RETRY_LIMIT;
};

export const incrementRetryCount = (
  config: InternalAuthRequestConfig,
): InternalAuthRequestConfig => {
  config._retryCount = (config._retryCount ?? 0) + 1;
  return config;
};

export const getAccessToken = (): string => {
  return toStringCookieValue("accessToken");
};

export const getRefreshToken = (): string => {
  return toStringCookieValue("refreshToken");
};

export const hasAccessToken = (): boolean => {
  return getAccessToken() !== "";
};

export const setAuthTokens = ({ access, refresh }: LoginResponse): void => {
  replaceCookie("accessToken", access);
  replaceCookie("refreshToken", refresh);
};

export const clearAuthTokens = (): void => {
  expireCookie("accessToken");
  expireCookie("refreshToken");
};

export const getUserAccessCookie = (): UserAccessCookie | null => {
  const cookieValue = readCookie("ua");

  if (
    cookieValue &&
    typeof cookieValue === "object" &&
    "isTeacher" in cookieValue &&
    "isStudent" in cookieValue &&
    "isStaff" in cookieValue &&
    "isSuperuser" in cookieValue
  ) {
    return cookieValue as UserAccessCookie;
  }

  return null;
};

export const setUserAccessCookie = (value: UserAccessCookie): void => {
  replaceCookie("ua", value);
};

export const clearUserAccessCookie = (): void => {
  expireCookie("ua");
};

export const clearAuthSession = (): void => {
  clearAuthTokens();
  clearUserAccessCookie();
};

export const getCurrentAuthSession = (): ViewAsStoredSession | null => {
  const access = getAccessToken();
  const refresh = getRefreshToken();

  if (!access || !refresh) {
    return null;
  }

  return {
    access,
    refresh,
    userAccess: getUserAccessCookie(),
    user: null,
    originalPath: null,
  };
};

export const restorePrimaryAuthSession = (
  session: Pick<ViewAsStoredSession, "access" | "refresh" | "userAccess">,
): void => {
  setAuthTokens(session);
  if (session.userAccess) {
    setUserAccessCookie(session.userAccess);
    return;
  }
  clearUserAccessCookie();
};

export const getViewAsBackupSession = (): ViewAsStoredSession | null => {
  const cookieValue = readCookie(VIEW_AS_BACKUP_COOKIE);

  if (
    !cookieValue ||
    typeof cookieValue !== "object" ||
    !("access" in cookieValue) ||
    !("refresh" in cookieValue)
  ) {
    return null;
  }

  return cookieValue as ViewAsStoredSession;
};

export const hasViewAsBackupSession = (): boolean => {
  return getViewAsBackupSession() !== null;
};

export const setViewAsBackupSession = (value: ViewAsStoredSession): void => {
  writeCookie(VIEW_AS_BACKUP_COOKIE, value);
};

export const clearViewAsBackupSession = (): void => {
  expireCookie(VIEW_AS_BACKUP_COOKIE);
};

export const getViewAsState = (): ViewAsStatePayload | null => {
  const cookieValue = readCookie(VIEW_AS_STATE_COOKIE);

  if (
    !cookieValue ||
    typeof cookieValue !== "object" ||
    !("active" in cookieValue)
  ) {
    return null;
  }

  return cookieValue as ViewAsStatePayload;
};

export const isViewAsActive = (): boolean => {
  return getViewAsState()?.active === true;
};

export const setViewAsState = (value: ViewAsStatePayload): void => {
  writeCookie(VIEW_AS_STATE_COOKIE, value);
};

export const clearViewAsState = (): void => {
  expireCookie(VIEW_AS_STATE_COOKIE);
};

export const getViewAsRecentSimulations = (): ViewAsRecentSimulation[] => {
  const cookieValue = readCookie(VIEW_AS_RECENT_SIMULATIONS_COOKIE);
  return Array.isArray(cookieValue)
    ? (cookieValue as ViewAsRecentSimulation[])
    : [];
};

export const setViewAsRecentSimulations = (
  value: ViewAsRecentSimulation[],
): void => {
  writeCookie(VIEW_AS_RECENT_SIMULATIONS_COOKIE, value);
};

export const clearViewAsRecentSimulations = (): void => {
  expireCookie(VIEW_AS_RECENT_SIMULATIONS_COOKIE);
};

export const clearViewAsStorage = (): void => {
  clearViewAsBackupSession();
  clearViewAsState();
  clearViewAsRecentSimulations();
};

export const hasActiveRefresh = (): boolean => {
  return refreshPromise !== null;
};

export const waitForActiveRefresh = async (): Promise<void> => {
  if (!refreshPromise) {
    return;
  }

  await refreshPromise;
};

export const beginRefresh = (
  refreshOperation: Promise<void>,
): Promise<void> => {
  refreshPromise = refreshOperation.finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
};

export const enqueueRetryRequest = (
  execute: () => Promise<AxiosResponse>,
): Promise<AxiosResponse> => {
  return new Promise((resolve, reject) => {
    retryQueue.push({
      execute,
      resolve,
      reject,
    });
  });
};

export const replayRetryQueue = async (): Promise<void> => {
  const queuedItems = retryQueue;
  retryQueue = [];

  await Promise.allSettled(
    queuedItems.map(async (queuedItem) => {
      try {
        const response = await queuedItem.execute();
        queuedItem.resolve(response);
      } catch (error) {
        queuedItem.reject(error);
      }
    }),
  );
};

export const rejectRetryQueue = (error: unknown): void => {
  const queuedItems = retryQueue;
  retryQueue = [];

  queuedItems.forEach((queuedItem) => {
    queuedItem.reject(error);
  });
};
